import { Badge, Card, DataTable, EmptyState, Skeleton, Stat, Callout } from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { useResource } from '../lib/hooks';
import { formatCost, formatMs, formatNumber, formatDateTime } from '../lib/format';

interface Dashboard {
  traffic: {
    last24h: { total: number; errors: number; successRate: number };
    last7d: { total: number; errors: number; successRate: number };
  };
  latency: { p50: number | null; p95: number | null; p99: number | null };
  failovers24h: number;
  unhealthyAccounts: Array<{
    id: string;
    label: string;
    healthState: string;
    lastErrorReason: string | null;
    lastErrorAt: string | null;
  }>;
  openBreakers: Array<{
    providerAccount: string;
    category: string;
    state: string;
    openedAt: string | null;
    lastReason: string | null;
  }>;
  topOperations: Array<{ operationKey: string | null; count: number }>;
  topClientApps: Array<{ clientApp: string | null; count: number }>;
  topErrors: Array<{ errorCode: string | null; count: number }>;
  usage30d: {
    records: number;
    totalTokens: number;
    estimatedCostUsd: number;
    unknownCostRecords: number;
  };
}

export function DashboardPage() {
  const { data, loading, error } = useResource<Dashboard>('/api/admin/dashboard');

  if (loading) return <Skeleton rows={6} />;
  if (error || !data) return <Callout tone="danger">Nie udało się wczytać pulpitu.</Callout>;

  const successPercent = (rate: number) => `${(rate * 100).toFixed(2)}%`;

  return (
    <>
      <PageHeader
        title="Pulpit"
        description="Bieżąca kondycja Hub: ruch, błędy, opóźnienia, przełączenia dostawców i koszty."
      />

      <div className="ah-grid cols-4" style={{ marginBottom: 16 }}>
        <Stat
          label="Żądania (24 h)"
          value={formatNumber(data.traffic.last24h.total)}
          note={`${formatNumber(data.traffic.last24h.errors)} błędów`}
        />
        <Stat
          label="Skuteczność (24 h)"
          value={successPercent(data.traffic.last24h.successRate)}
          note={`7 dni: ${successPercent(data.traffic.last7d.successRate)}`}
        />
        <Stat
          label="Opóźnienie p95 (24 h)"
          value={formatMs(data.latency.p95)}
          note={`p50 ${formatMs(data.latency.p50)} · p99 ${formatMs(data.latency.p99)}`}
        />
        <Stat
          label="Przełączenia dostawcy (24 h)"
          value={formatNumber(data.failovers24h)}
          note="liczba prób zakończonych failoverem"
        />
      </div>

      <div className="ah-grid cols-2">
        <Card
          title="Niezdrowe połączenia"
          description="Konta oznaczone jako DEGRADED lub UNHEALTHY."
          tight
        >
          <DataTable
            rows={data.unhealthyAccounts}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Wszystkie połączenia sprawne"
                description="Żadne konto dostawcy nie jest oznaczone jako niezdrowe."
              />
            }
            columns={[
              {
                key: 'label',
                header: 'Konto',
                render: (row) => <span className="ah-mono">{row.label}</span>,
              },
              {
                key: 'state',
                header: 'Stan',
                render: (row) => (
                  <Badge tone={row.healthState === 'UNHEALTHY' ? 'danger' : 'warn'}>
                    {row.healthState}
                  </Badge>
                ),
              },
              {
                key: 'reason',
                header: 'Ostatni błąd',
                render: (row) => (
                  <span title={row.lastErrorReason ?? ''}>
                    {row.lastErrorReason ? `${row.lastErrorReason.slice(0, 60)}…` : '—'}
                    <br />
                    <small style={{ color: 'var(--ah-text-subtle)' }}>
                      {formatDateTime(row.lastErrorAt)}
                    </small>
                  </span>
                ),
              },
            ]}
          />
        </Card>

        <Card
          title="Otwarte bezpieczniki"
          description="Wyłączone trasy do czasu wygaśnięcia cooldownu."
          tight
        >
          <DataTable
            rows={data.openBreakers}
            rowKey={(row) => `${row.providerAccount}:${row.category}`}
            empty={<EmptyState title="Brak otwartych bezpieczników" />}
            columns={[
              {
                key: 'account',
                header: 'Konto',
                render: (row) => <span className="ah-mono">{row.providerAccount}</span>,
              },
              { key: 'category', header: 'Kategoria', render: (row) => row.category },
              {
                key: 'state',
                header: 'Stan',
                render: (row) => (
                  <Badge tone={row.state === 'OPEN' ? 'danger' : 'warn'}>{row.state}</Badge>
                ),
              },
              { key: 'reason', header: 'Powód', render: (row) => row.lastReason ?? '—' },
            ]}
          />
        </Card>

        <Card title="Najczęstsze operacje (24 h)" tight>
          <DataTable
            rows={data.topOperations}
            rowKey={(row) => row.operationKey ?? 'unknown'}
            empty={<EmptyState title="Brak ruchu" />}
            columns={[
              {
                key: 'operation',
                header: 'Operacja',
                render: (row) => <span className="ah-mono">{row.operationKey ?? '—'}</span>,
              },
              {
                key: 'count',
                header: 'Liczba',
                align: 'right',
                render: (row) => formatNumber(row.count),
              },
            ]}
          />
        </Card>

        <Card title="Najczęstsze błędy (7 dni)" tight>
          <DataTable
            rows={data.topErrors}
            rowKey={(row) => row.errorCode ?? 'unknown'}
            empty={
              <EmptyState
                title="Brak błędów"
                description="W ostatnich 7 dniach nie zarejestrowano żadnego błędu."
              />
            }
            columns={[
              {
                key: 'code',
                header: 'Kod',
                render: (row) => <Badge tone="danger">{row.errorCode}</Badge>,
              },
              {
                key: 'count',
                header: 'Liczba',
                align: 'right',
                render: (row) => formatNumber(row.count),
              },
            ]}
          />
        </Card>

        <Card title="Ruch według aplikacji (24 h)" tight>
          <DataTable
            rows={data.topClientApps}
            rowKey={(row) => row.clientApp ?? 'unknown'}
            empty={<EmptyState title="Brak ruchu" />}
            columns={[
              {
                key: 'app',
                header: 'Aplikacja',
                render: (row) => <span className="ah-mono">{row.clientApp ?? '—'}</span>,
              },
              {
                key: 'count',
                header: 'Liczba',
                align: 'right',
                render: (row) => formatNumber(row.count),
              },
            ]}
          />
        </Card>

        <Card title="Zużycie i koszty (30 dni)">
          <div className="ah-grid cols-2">
            <Stat label="Tokeny" value={formatNumber(data.usage30d.totalTokens)} />
            <Stat
              label="Szacowany koszt"
              value={formatCost(data.usage30d.estimatedCostUsd, 'KNOWN')}
              note={`${formatNumber(data.usage30d.unknownCostRecords)} rekordów bez reguły cenowej`}
            />
          </div>
          {data.usage30d.unknownCostRecords > 0 && (
            <Callout tone="warn">
              Część zużycia nie ma dopasowanej reguły cenowej i jest raportowana jako koszt
              nieznany. Uzupełnij reguły cenowe zamiast zgadywać wartość.
            </Callout>
          )}
        </Card>
      </div>
    </>
  );
}
